import { z } from 'zod';
/**
 * Fields an organisation declares for its contacts.
 *
 * `key` is what a segment rule and a merge token name; `label` is what a
 * person reads. The shape is checked here and the meaning in the domain —
 * whether the key is a slug, and whether it collides with something
 * `renderMessage` already fills from the recipient.
 */
/** Not exported: every consumer reaches these through `ContactFieldDto['type']`. */
const contactFieldTypeSchema = z.enum([
    'text',
    'number',
    'date',
    'boolean',
    'email',
    'phone',
    'url',
    'dropdown',
    'status',
]);
/**
 * What a dropdown or a status may be set to.
 *
 * Empty for every other kind. The domain refuses a list on a kind that is not
 * chosen from one, rather than ignoring it — a list nothing enforces is a
 * promise the board does not keep.
 *
 * The two limits are also the domain's `MAX_CHOICES` and `MAX_CHOICE_LENGTH`,
 * which is what stops an import creating options past what this allows. They
 * have to agree: a stored field wider than this is one the browser refuses to
 * parse, which breaks the whole field listing rather than the one column.
 * `choicesCapMatchesContract.test.ts` in `packages/adapter-http` holds them
 * together, because that is the package that can see both.
 */
const choicesSchema = z.array(z.string().trim().min(1).max(60)).max(50);
export const contactFieldSchema = z.object({
    key: z.string().max(40),
    label: z.string().max(120),
    type: contactFieldTypeSchema,
    choices: choicesSchema,
});
export const contactFieldListSchema = z.object({ fields: z.array(contactFieldSchema) });
export const declareFieldRequestSchema = z.object({
    label: z.string().trim().min(1, 'a field needs a label').max(120),
    /**
     * Optional, and derived from the label when it is left out.
     *
     * The key is what every saved rule and every merge token names, so it has
     * to be choosable — and equally, most people should never have to think
     * about it. Deriving it on the server rather than in the browser keeps one
     * implementation of what a key is, in the domain, where the rule that
     * validates it also lives.
     */
    key: z.string().trim().max(40).optional(),
    type: contactFieldTypeSchema,
    /** Left out is none, which is what every kind but a dropdown or a status has. */
    choices: choicesSchema.optional(),
});
/**
 * Declaring answers one of three ways, and a form acts on each.
 *
 * `invalid` carries the domain’s reason — a key that is not a slug, or one
 * `renderMessage` already fills — because those are things to show beside the
 * field rather than a protocol-level refusal.
 */
export const declareFieldResponseSchema = z.discriminatedUnion('status', [
    /** Carries the field as stored, so the browser can show the key it was given. */
    z.object({ status: z.literal('declared'), field: contactFieldSchema }),
    z.object({ status: z.literal('key-taken') }),
    z.object({ status: z.literal('invalid'), problem: z.string() }),
]);
/**
 * Renaming a field, and changing what kind it is.
 *
 * **The key is not here.** Every saved segment rule and every `{{token}}` names
 * the key, so a rename that could change it would silently alter who a campaign
 * reaches. The label is what a person reads and is the only name that moves.
 *
 * Both fields are sent, because the menu that changes one shows the other — and
 * a partial update is a second shape for one write.
 */
export const editFieldRequestSchema = z.object({
    label: z.string().trim().min(1, 'a column needs a name').max(120),
    type: contactFieldTypeSchema,
    /** Left out is none, which is what every kind but a dropdown or a status has. */
    choices: choicesSchema.optional(),
});
/**
 * What an organisation calls a column it did not create.
 *
 * The five the board always has are not rows anywhere — they are the contacts
 * table's own columns — so a rename is stored against the column's id. Deleting
 * that row is what puts the default name back, which is why there is no way to
 * send one.
 */
export const renameColumnRequestSchema = z.object({
    label: z.string().trim().min(1, 'a column needs a name').max(120),
});
/** Keyed by column id. An absent entry is that column's default label. */
export const columnLabelsSchema = z.object({
    labels: z.record(z.string().max(80), z.string().max(120)),
});
//# sourceMappingURL=contactFields.js.map